import { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Circle, CircleCheck, CircleDashed, GitPullRequest, Hand, LoaderCircle, type LucideIcon } from 'lucide-react';
import type { TaskCard, TaskColumn } from '@shared/types/tasks';
import { TaskActiveRunsContext } from '@/components/factory/active-runs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useFormatTaskRef } from '@/lib/current-project';
import { useBoardPath } from '@/navigation/project-paths';
import { cn } from '@/lib/utils';
import { isSubtaskBuilt, subtaskEdges, subtaskNodeState, type SubtaskNodeState } from './subtasks';
import { fromParentState } from './subtask-return';

/** SF-T-279: how each state looks, in the key and on its sub-task. */
const STATES: Record<SubtaskNodeState, { label: string; hint: string; icon: LucideIcon; className: string; iconClassName?: string }> = {
  building: {
    label: 'Building',
    hint: 'A run is going on it, or it’s with the AI builder',
    icon: LoaderCircle,
    className: 'border-info-border bg-info-soft text-info-strong',
    iconClassName: 'motion-safe:animate-spin',
  },
  needs_person: { label: 'Needs a person', hint: 'It waits on someone: its questions, or a run that failed', icon: Hand, className: 'border-warning-border bg-warning-soft text-warning-strong' },
  pr_ready: { label: 'PR ready', hint: 'Its PR is up and its run has finished: ready to review and merge', icon: GitPullRequest, className: 'border-success-border bg-success-soft text-success-strong' },
  can_start: { label: 'Can start', hint: 'What it waits on has PRs: it goes next', icon: Circle, className: 'border-foreground/30 bg-background text-foreground' },
  waiting: { label: 'Waiting', hint: 'It waits on sub-tasks without a PR yet', icon: CircleDashed, className: 'border-dashed bg-background text-muted-foreground' },
  done: { label: 'Done', hint: 'Done, or its PR is merged', icon: CircleCheck, className: 'border-border bg-muted text-muted-foreground' },
};
const ORDER: SubtaskNodeState[] = ['building', 'needs_person', 'pr_ready', 'can_start', 'waiting', 'done'];

/**
 * SF-T-268, SF-T-279: the sub-tasks' dependencies as steps, top to bottom, read-only: step 1 holds the ones that wait on
 * nothing, and each other one sits a step past the furthest one it waits on (`lanes`: subtaskLanes of the same list), in
 * the list's order. Each sub-task says where it stands (subtaskNodeState: building, needs a person, PR ready, can start,
 * waiting or done), in colour with an icon, and the key on top counts them. Pointing at one (or focusing it) picks out
 * the ones it waits on and fades the rest. Each opens its sub-task. Beside TaskSubtasks' list on wide screens; a
 * suggested order (SF-T-271) draws its own, outlining what it changes in the AI's colour. SF-T-279: `foldable` (above the
 * list on a phone) starts folded to its caption and key, which open it.
 */
export function SubtaskGraph({
  subtasks,
  columns,
  lanes,
  suggested,
  foldable = false,
  className,
}: {
  subtasks: TaskCard[];
  columns: readonly TaskColumn[];
  lanes: Map<string, number>;
  /**
   * SF-T-271: this is the AI's suggested order (Suggest the order), not today's: the waits it adds (`dep>task`) show in
   * the AI's colour when one is pointed at, and the sub-tasks it changes or moves are outlined in it.
   */
  suggested?: { edges: Set<string>; nodes: Set<string> };
  /** Folded to its caption and key until pressed (above the list on a narrow pane). */
  foldable?: boolean;
  className?: string;
}) {
  const formatRef = useFormatTaskRef();
  const boardPath = useBoardPath();
  const location = useLocation();
  // The Board's live runs, when it's open there, as well as the task detail's run_going.
  const activeRuns = useContext(TaskActiveRunsContext);
  const [pointed, setPointed] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const byId = new Map(subtasks.map((t) => [t.id, t]));
  const edges = subtaskEdges(subtasks);
  const depsOf = (id: string) => (edges.get(id)?.after ?? []).flatMap((d) => byId.get(d) ?? []);
  const states = new Map(
    subtasks.map((t) => [t.id, subtaskNodeState({ ...t, run_going: t.run_going || activeRuns.has(t.id) }, columns, depsOf(t.id))])
  );
  const steps: TaskCard[][] = [];
  for (const task of subtasks) (steps[lanes.get(task.id) ?? 0] ??= []).push(task);
  const counts = ORDER.flatMap((state) => {
    const n = subtasks.filter((t) => states.get(t.id) === state).length;
    return n ? [{ state, n }] : [];
  });
  const pickedDeps = new Set(pointed ? (edges.get(pointed)?.after ?? []) : []);

  const caption = (
    <span className={cn('text-2xs', suggested ? 'text-ai-strong' : 'text-muted-foreground')}>
      {suggested ? 'As suggested: what it changes in the AI’s colour' : 'Steps: each starts once the ones it’s after have a PR'}
    </span>
  );
  // The key: how many are in each state.
  const key = (
    <ul className="flex flex-wrap gap-x-2.5 gap-y-1 text-2xs text-muted-foreground" aria-label="Where they stand">
      {counts.map(({ state, n }) => {
        const meta = STATES[state];
        const Icon = meta.icon;
        return (
          <li key={state} className="inline-flex items-center gap-1" data-hint={meta.hint}>
            <span className={cn('inline-flex size-4 items-center justify-center rounded border', meta.className)}>
              <Icon className="size-2.5" aria-hidden />
            </span>
            {n} {meta.label.toLowerCase()}
          </li>
        );
      })}
    </ul>
  );
  const list = (
    <ol className="space-y-0">
      {steps.map((step, i) =>
        step ? (
          <li key={i} className="relative flex gap-2 pb-2 last:pb-0">
            {/* The rail: each step's number, joined to the next. */}
            <span className="relative flex w-4 shrink-0 justify-center">
              <span className="z-10 flex size-4 items-center justify-center rounded-full border bg-background text-3xs tabular-nums text-muted-foreground">{i + 1}</span>
              {i < steps.length - 1 ? <span className="absolute top-4 -bottom-2 w-px bg-border" aria-hidden /> : null}
            </span>
            <span className="flex min-w-0 flex-1 flex-wrap gap-1">
              {step.map((task) => {
                const state = states.get(task.id)!;
                const meta = STATES[state];
                const Icon = meta.icon;
                const deps = depsOf(task.id);
                const ref = formatRef(task.number);
                const waits = deps.map((d) => `${formatRef(d.number)}${isSubtaskBuilt(d, columns) ? '' : ' (not built yet)'}`);
                const faded = pointed !== null && pointed !== task.id && !pickedDeps.has(task.id);
                const addedWait = pointed !== null && suggested?.edges.has(`${task.id}>${pointed}`);
                return (
                  <Link
                    key={task.id}
                    to={boardPath(task.id)}
                    state={fromParentState(location.state)}
                    onPointerEnter={() => setPointed(task.id)}
                    onPointerLeave={() => setPointed((p) => (p === task.id ? null : p))}
                    onFocus={() => setPointed(task.id)}
                    onBlur={() => setPointed((p) => (p === task.id ? null : p))}
                    data-hint={`${ref} ${task.title}: ${meta.label.toLowerCase()}${waits.length ? `; after ${waits.join(', ')}` : ''}`}
                    aria-label={[`${ref} ${task.title}`, meta.label.toLowerCase(), waits.length ? `after ${waits.join(', ')}` : 'waits on nothing'].join('; ')}
                    data-state={state}
                    className={cn(
                      'inline-flex h-6 items-center gap-1 rounded border px-1.5 text-2xs tabular-nums transition-opacity hover:border-foreground/40',
                      meta.className,
                      state === 'done' && 'line-through',
                      faded && 'opacity-40',
                      pickedDeps.has(task.id) && 'ring-2 ring-foreground/30',
                      addedWait && 'ring-2 ring-ai',
                      suggested?.nodes.has(task.id) && 'border-ai-border ring-1 ring-ai-border'
                    )}
                  >
                    <Icon className={cn('size-3 shrink-0', meta.iconClassName)} aria-hidden />
                    {ref}
                  </Link>
                );
              })}
            </span>
          </li>
        ) : null
      )}
    </ol>
  );

  if (foldable) {
    return (
      <Collapsible open={open} onOpenChange={setOpen} className={cn('rounded-md border', className)} data-testid="subtask-graph">
        <CollapsibleTrigger className="flex w-full items-start gap-1.5 rounded-md p-2 text-left hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={open ? 'Hide the steps' : 'Show the steps'}>
          <ChevronRight className={cn('mt-0.5 size-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} aria-hidden />
          <span className="min-w-0 flex-1 space-y-1">
            {caption}
            {key}
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className="px-2 pb-2">
          <figure aria-label="Sub-task order">{list}</figure>
        </CollapsibleContent>
      </Collapsible>
    );
  }
  return (
    <figure className={cn('space-y-2', className)} aria-label="Sub-task order" data-testid="subtask-graph">
      <figcaption>{caption}</figcaption>
      {key}
      {list}
    </figure>
  );
}
